import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, Flex, Grid } from "@chakra-ui/react";
import BestSellingProductCard from "@/features/dashboard/features/best-selling-section/components/best-selling-product-card";
import { BestSellingProducts } from "@/features/dashboard/features/best-selling-section/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import useGetBestSellingProducts from "@/features/dashboard/features/best-selling-section/hooks/useGetBestSellingProducts";
import NoResults from "@/shared/components/no-results";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useSearchParams } from "react-router-dom";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  // brandIdsBs: number[];
  showOrderable: boolean;
  isOrderable: boolean;
  clear: () => void;
}

function BestSellingGrid({ showOrderable, isOrderable, clear }: Props) {
  const identity = useIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<BestSellingProducts[]>([]);
  const [extraProducts, setExtraProducts] = useState<BestSellingProducts[]>([]);
  const { tSentence } = useI18nHelper();

  useEffect(() => {
    const ids = searchParams.getAll("brand_ids").map((id) => Number(id));
    setBrandIds(ids);
  }, [searchParams]);

  const { bestSellingProducts } = useGetBestSellingProducts({
    // brand_ids: brandIds && brandIds.length > 0 ? brandIds : [],
  });

  useEffect(() => {
    let filtered: BestSellingProducts[] = [];
    let extra: BestSellingProducts[] = [];
    if (brandIds && brandIds.length > 0) {
      filtered = bestSellingProducts.filter(
        (product: { brand_id: number }) => brandIds.includes(product.brand_id)
      ).map(
        (product: any) => ({
          ...product,
          highlight: true,
        }
      ));
      extra = bestSellingProducts.filter(
        (product: { brand_id: number }) => !brandIds.includes(product.brand_id)
      );
    } else {
      filtered = bestSellingProducts;
    }
    if (isOrderable) {
      filtered = filtered.filter(
        (product: {
          in_valid_line_sheet: boolean;
          in_valid_api_order: boolean;
        }) =>
          product.in_valid_line_sheet === true ||
          product.in_valid_api_order === true
      );
      extra = extra.filter(
        (product: {
          in_valid_line_sheet: boolean;
          in_valid_api_order: boolean;
        }) =>
          product.in_valid_line_sheet === true ||
          product.in_valid_api_order === true
      ).map((product) => ({
        ...product,
        highlight: true,
      }));
    }
    setFilteredProducts(filtered);
    setExtraProducts(extra);
  }, [bestSellingProducts, brandIds, isOrderable]);

  const parentRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);

  // 1) 컨테이너 너비 변할 때마다 컬럼 수 계산
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const MIN_CELL = 96;
    const GAP = 12; // grid gap
    const PAD_X = 16;
    const ro = new ResizeObserver((entries) => {
      const totalW = entries[0].contentRect.width;
      // 실제 칼럼이 들어갈 수 있는 너비
      const usableW = totalW - PAD_X;
      // +GAP, /(MIN+GAP) 공식을 쓰면
      // “남는 여유 폭이 (MIN_CELL+GAP) 단위로 떨어졌을 때만
      //  칼럼 개수가 올라간다” 는 효과가 납니다.
      const cols = Math.max(1, Math.floor((usableW + GAP) / (MIN_CELL + GAP)));
      setColumns(cols > 5 ? 5 : cols); // 최대 5개 컬럼
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 2) 총 행 수
  const rowCount = Math.ceil(filteredProducts.length / 5) + Math.ceil(extraProducts.length / 5);
  const imgRef = useRef<ResizeObserver>();
  const [dynamicWidth, setDynamicWidth] = useState(0);

  // 3) Virtualizer 설정 (행 단위)
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 250 + dynamicWidth, // 대략 한 행의 높이(px)
    overscan: 2,
  });

  const imgRefCallback = useCallback(
    (node: HTMLImageElement | null) => {
      if (imgRef.current) {
        imgRef.current.disconnect();
        imgRef.current = undefined;
      }
      const MIN_IMG = 96; // 이미지 최소 너비(px)
      const MAX_IMG = 151; // 이미지 최대 너비(px)
      const MAX_OUT = 40; // dynamicWidth 최대값

      if (!node) return;

      const updateDynamicWidth = () => {
        const w = node.clientWidth;
        // 1) (w - MIN_IMG) * (MAX_OUT / (MAX_IMG - MIN_IMG))
        const proportional = ((w - MIN_IMG) * MAX_OUT) / (MAX_IMG - MIN_IMG);

        // 2) 0~MAX_OUT 범위로 클램핑
        const clamped = Math.max(0, Math.min(proportional, MAX_OUT));

        setDynamicWidth(clamped);
      };

      // 초기 마운트 시 한 번 계산
      updateDynamicWidth();

      // 만약 이미지 크기가 리사이즈될 수 있다면, ResizeObserver를 써서 감지할 수 있다.
      const ro = new ResizeObserver(() => {
        updateDynamicWidth();
        rowVirtualizer.measure();
      });
      ro.observe(node);

      imgRef.current = ro;
    },
    [rowVirtualizer]
  );

  return (
    <Box ref={parentRef} overflowY="auto">
      {
        brandIds.length > 0 && filteredProducts.length === 0 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            gap="8px"
            padding={"30px 4px 24px 16px"}
            marginBottom={"24px"}
            marginX="16px"
            borderBottom={"0.5px solid #8F8F8C"}
          >
            <Box color={"#D4D4D1"}>
              <SVG
                src={"/icons-v1_1/warning-triangle-solid.svg"}
                width={20}
                height={20}
              />
            </Box>
            <Box textStyle={"body-md-bold"} color={"#444444"}>
              {tSentence("dashboard\:best_selling_no_result")}
            </Box>
          </Flex>
        )
      }
      {bestSellingProducts.length === 0 ? (
        <Box padding={"44px"}>
          <NoResults handleResetFilter={clear} />
        </Box>
      ) : (
        <Box
          h={`${rowVirtualizer.getTotalSize()}px`}
          position="relative"
          key={dynamicWidth}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow, rowIdx) => {
            const rowIndex = virtualRow.index;
            const start = virtualRow.start;
            const from = rowIndex * 5;
            const to = Math.min(from + 5, filteredProducts.length);
            let rowItems = filteredProducts.slice(from, to);
            if (rowItems.length == 0) {
              const extraFrom = (rowIndex - Math.ceil(filteredProducts.length / 5)) * 5;
              const extraTo = Math.min(extraFrom + 5, extraProducts.length);
              rowItems = rowItems.concat(extraProducts.slice(extraFrom, extraTo));
            }

            return (
              <Grid
                key={rowIndex}
                templateColumns={`repeat(${5}, 1fr)`}
                gap="12px"
                position="absolute"
                transform={`translateY(${start}px)`}
                width="100%"
                padding={"0px 4px 10px 16px"}
                ref={rowVirtualizer.measureElement}
                borderBottom={ rowIndex + 1 === Math.ceil(filteredProducts.length / 5) ? "0.5px solid #8F8F8C" : "none"}
              >
                {rowItems.map((item: BestSellingProducts, index: number) => (
                  <BestSellingProductCard
                    key={item.product_name + item.brand_name + item.ranking}
                    product={item}
                    showOrderable={showOrderable}
                  >
                    <Flex flexDirection="column" gap="8px">
                      <BestSellingProductCard.Image imgRef={imgRefCallback} />
                      <Flex flexDirection="column" gap="2px" color="#444444">
                        <BestSellingProductCard.Name />
                        <BestSellingProductCard.Sku />
                        <BestSellingProductCard.Price />
                      </Flex>
                    </Flex>
                  </BestSellingProductCard>
                ))}
              </Grid>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default BestSellingGrid;